import React from "react";
import PropTypes from "prop-types";
import Network from "../../network/network";
import PingProducer from "../../network/pingProducer";
import TimerSynchronizer from "../../network/timerSynchronizer";
import DateUtils from "../../utils/dateUtils";

const RECONNECT_DELAY = 5000; // 5 seconds
const TABLE_UPDATE_INTERVAL = 15000; // 15 seconds
const PLAYER_INFO_INTERVAL = 10000; // 10 seconds
const SERVER_INFO_INTERVAL = 8000; // 8 seconds
const SERVER_TIME_INTERVAL = 10000; // 10 seconds
const BBJ_UPDATE_INTERVAL = 60000; // 60 seconds

export default class LobbyBase extends React.Component {
    static propTypes = {
        data: PropTypes.shape({
            sid: PropTypes.string.isRequired
        }).isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            lobbyNetworkReady: false,
            connectionStatus: "disconnected", // disconnected, connecting, connected, reconnecting, error
            lastError: null
        };

        this.socketName = "Lobby";
        this.intervals = {};
        this.pendingRequests = new Set();
        this.preventReconnect = false;
        const { width, height } = window.screen;
        this.screenDimensions = { width, height };
    }

    componentDidMount() {
        this.initNetwork();
    }

    componentWillUnmount() {
        this.cleanupNetwork();
    }

    initNetwork() {
        this.setState({ connectionStatus: "connecting" });

        try {
            this._lobbyNetwork = new Network();
            this._lobbyNetwork.initNetwork(this.socketName, this.getEventCallbacks());

            this._pingProducer = new PingProducer(this._lobbyNetwork);
            this._timerSynchronizer = new TimerSynchronizer(this._lobbyNetwork);
            this._dateUtils = new DateUtils();

            this.initRecurringOperations();
        } catch (error) {
            console.error("Network initialization failed:", error);
            this.setState({ connectionStatus: "error", lastError: error });
            this.scheduleReconnect();
        }
    }

    // cleanupNetwork() {
    //     Object.values(this.intervals).forEach(interval => clearInterval(interval));
    //     this.intervals = {};

    //     this._pingProducer?.stop();
    //     this._timerSynchronizer?.stop();

    //     if (this._lobbyNetwork) {
    //         this._lobbyNetwork.close();
    //         this._lobbyNetwork = null;
    //     }

    //     this.setState({ lobbyNetworkReady: false, connectionStatus: "disconnected" });
    // }

    cleanupNetwork() {
        Object.values(this.intervals).forEach(interval => clearInterval(interval));
        this.intervals = {};

        this._pingProducer?.stop();
        this._timerSynchronizer?.stop();

        if (this._lobbyNetwork) {
            this._lobbyNetwork.close();
            this._lobbyNetwork = null;
        }

        clearTimeout(this.reconnectTimeout); // ⬅️ clear existing reconnects
        this.reconnectTimeout = null;

        this.setState({ lobbyNetworkReady: false, connectionStatus: "disconnected" });
    }



    getEventCallbacks() {
        return {
            open: this.handleConnectionOpen.bind(this),
            message: this.handleMessage.bind(this),
            close: this.handleConnectionClose.bind(this),
            error: this.handleConnectionError.bind(this),
            reConnect: this.handleReconnection.bind(this),
        };
    }

    handleConnectionOpen() {
        this.setState({ connectionStatus: "connected", lastError: null });
        this.authenticateWithLobby();

        this._pingProducer.start();
        this._timerSynchronizer.start();
        this.notifyConnectionStatus("connected");

        this.lobbyResponseTimeout = setTimeout(() => {
            if (!this.state.lobbyNetworkReady) {
                console.warn("No LobbyInfo received — logging out for safety.");
                this.onGetError({
                    Error: {
                        attr: {
                            code: "002",
                            description: "Session possibly replaced or timed out"
                        }
                    }
                });
            }
        }, 5000); // 5s timeout to receive LobbyInfo
    }


    handleConnectionError(error) {
        console.error("Connection error:", error);
        this.setState({ connectionStatus: "error", lastError: error });
        this.notifyConnectionStatus("error");
        this.scheduleReconnect();
    }

    handleReconnection() {
        this.setState({ connectionStatus: "reconnecting" });
        this.notifyConnectionStatus("reconnecting");

        this._pingProducer.stop();
        this._timerSynchronizer.stop();

        this.authenticateWithLobby();
        this._pingProducer.start();
        this._timerSynchronizer.start();

        this.setState({ connectionStatus: "connected" });
        this.notifyConnectionStatus("reconnected");
    }

    handleConnectionClose() {
        this.setState({ connectionStatus: "disconnected" });
        this._pingProducer.stop();
        this._timerSynchronizer.stop();
        this.notifyConnectionStatus("closed");
        this.scheduleReconnect();
    }

    // scheduleReconnect() {
    //     if (this.reconnectTimeout) {
    //         clearTimeout(this.reconnectTimeout);
    //     }

    //     this.reconnectTimeout = setTimeout(() => {
    //         this.initNetwork();
    //     }, RECONNECT_DELAY);
    // }

    scheduleReconnect() {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }

        if (this.preventReconnect) return;

        this.reconnectTimeout = setTimeout(() => {
            this.initNetwork();
        }, RECONNECT_DELAY);
    }



    authenticateWithLobby() {
        const { sid } = this.props.data;
        console.log("Sending <EnterLobby> with sessionId:", sid);
        const authMessage = `<EnterLobby name="POKER" sessionId="${sid}" />`;
        this.sendSocketMessage(authMessage);
    }


    handleMessage(data) {
        try {
            this._pingProducer.onAsynchData(data);
            this._timerSynchronizer.onAsynchData(data);

            const messageHandlers = {
                LobbyInfo: this.onLobbyInfo,
                ServerInfo: this.onServerInfo,
                ServerTime: this.onServerTime,
                PlayerInfo: this.onPlayerInfo,
                Avatars: this.onGetAvatarList,
                SingleTables: this.onGetTables,
                TableDetails: this.onGetTableDetails,
                BadBeatJackpotPayout: this.onBadBeatJackpotPayout,
                Error: this.handleErrorMessage,
                ConnectionReplaced: this.handleConnectionReplaced,
                PlayerJoinedWaitingList: this.onPlayerJoinedWaitingList,
                OpenTable: this.onOpenTable,
                Tournaments: this.onGetTournaments,
                OpenTournamentLobby: this.onOpenTournamentLobby,
                TournamentPlayerRegistered: this.onTournamentPlayerRegistered,
                TournamentPlayerUnregistered: this.onTournamentPlayerUnregistered,
                NewsList: this.onGetNews,
                DpSetting: this.onGetDPSettings,
                PlayerLevelInfo: this.onGetPlayerLevelInfo,
                MyTables: this.onGetMyTable,
                MyTournaments: this.onGetMyTournaments,
                PlayerSearchResult: this.onGetPlayerSearchResult,
                BadBeatJackpot: this.onGetBadBeatJackpot,
                Buddies: this.onGetBuddies,
                AcceptInvitation: this.onAcceptInvitation,
                BuddyRemoved: this.onBuddyRemoved,

                MoneyExchangeInfo: this.onMoneyExchangeInfo,
            };

            for (const [key, handler] of Object.entries(messageHandlers)) {
                if (data.hasOwnProperty(key)) {
                    handler.call(this, { [key]: data[key] });
                }
            }
        } catch (error) {
            console.error("Error processing message:", error, data);
        }
    }

    handleErrorMessage(data) {
        if (data.Error.attr.code === "040") {
            this.initNetwork();
        } else {
            this.onGetError({ Error: data.Error });
        }
    }

    handleConnectionReplaced() {
        this.onGetError({ Error: "ConnectionReplaced" });
        this.cleanupNetwork();
    }

    sendSocketMessage(message) {
        if (!this._lobbyNetwork) {
            // console.warn("Attempted to send message while network is not available");
            return;
        }

        try {
            this._lobbyNetwork.send(message);
        } catch (error) {
            console.error("Failed to send message:", error);
            this.handleConnectionError(error);
        }
    }

    initRecurringOperations() {
        this.intervals.getTables = setInterval(() => {
            this.sendSocketMessage(this.getTableRequest);
        }, TABLE_UPDATE_INTERVAL);

        this.intervals.playerInfo = setInterval(() => {
            this.sendSocketMessage("<GetPlayerInfo/>");
            this.sendSocketMessage("<GetNewsList/>");
        }, PLAYER_INFO_INTERVAL);

        this.intervals.serverInfo = setInterval(() => {
            this.sendSocketMessage("<GetServerInfo/>");
        }, SERVER_INFO_INTERVAL);

        this.intervals.serverTime = setInterval(() => {
            this.sendSocketMessage("<GetServerTime/>");
        }, SERVER_TIME_INTERVAL);

        this.intervals.bbjUpdate = setInterval(() => {
            this.sendSocketMessage("<GetBadBeatJackpot/>");
        }, BBJ_UPDATE_INTERVAL);
    }

    onLobbyInfo(data) {
        clearTimeout(this.lobbyResponseTimeout);

        const initialRequests = [
            "<GetBuddies/>",
            "<GetPlayerInfo/>",
            "<GetMyTables/>",
            "<GetNewsList/>",
            "<GetMyTournaments/>",
            "<GetServerInfo/>",
            "<GetBadBeatJackpot/>",
            "<GetAvatars/>",
            `<GetAvatar network="rp1" id="1-3b5"/>`
        ];

        initialRequests.forEach(request => this.sendSocketMessage(request));

        this.getTables();
        this.setState({ lobbyNetworkReady: true });
    }


    getTables() {
        const requestId = this._dateUtils.getUniqueIdentifier();
        this.getTableRequest = `<GetSingleTables id="${requestId}" modes="CASH" games="TEXAS_HOLDEM | OMAHA | OMAHA_FIVE_CARDS | OMAHA_SIX_CARDS | OMAHA_HIGH_LOW" />`;
        this.sendSocketMessage(this.getTableRequest);
    }

    updateGetTableRequest(data) {
        this.getTableRequest = data;
    }

    notifyConnectionStatus(status) {
        console.log("================= Socket Status =================");
        console.log(status);
        console.log("================= Socket Status =================");
    }

    onGetBadBeatJackpot(data) { }
    onGetBuddies(data) { }
    onAcceptInvitation(data) { }
    onBuddyRemoved(data) { }
    onMoneyExchangeInfo(data) { }
    onServerInfo(data) { }
    onServerTime(data) { }
    onPlayerInfo(data) { }
    onGetAvatarList(data) { }
    onGetTables(data) { }
    onGetTournaments(data) { }
    onGetTableDetails(data) { }
    onBadBeatJackpotPayout(data) { }
    onGetError(data) { }
    onGetNews(data) { }
    onGetDPSettings(data) { }
    onGetPlayerLevelInfo(data) { }
    onGetMyTable(data) { }
    onGetMyTournaments(data) { }
    onGetPlayerSearchResult(data) { }
    onTournamentPlayerRegistered(data) { }
    onTournamentPlayerUnregistered(data) { }
    onPlayerJoinedWaitingList(data) { }

    // onOpenTable(data) {
    //     // Implement table opening logic in child component
    // }

    // onOpenTournamentLobby(data) {
    //     // Implement tournament lobby opening logic in child component
    // }
}